import React, { useEffect, useState } from "react";
import { Award, FileText, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "./ui/dialog";
import HtmlContent from "./HtmlContent";

export function getCertificatePdfUrl(cert) {
  if (cert.fileType !== "pdf") return null;
  return cert.documentUrl || cert.fileUrl || null;
}

export function CertificateThumbnail({ cert, className = "" }) {
  const [imageError, setImageError] = useState(false);
  const isPdf = cert.fileType === "pdf";
  const thumbnailUrl = cert.iconUrl || (cert.fileType === "image" ? cert.image : null);
  const showImage = Boolean(thumbnailUrl) && !imageError;
  const isIconThumbnail = Boolean(cert.iconUrl) && !imageError;

  return (
    <div className={`relative overflow-hidden bg-[#e8f2fc] ${className}`}>
      {showImage ? (
        <img
          src={thumbnailUrl}
          alt={cert.title}
          onError={() => setImageError(true)}
          className={`w-full h-full ${isIconThumbnail ? "object-contain p-6" : "object-cover"}`}
          loading="lazy"
        />
      ) : isPdf ? (
        <div className="w-full h-full min-h-[160px] flex flex-col items-center justify-center gap-2 px-4 text-center bg-gradient-to-br from-slate-100 to-[#e8f2fc]">
          <FileText className="w-14 h-14 text-[#1e6fcf]/70" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#1758a8]">PDF</span>
        </div>
      ) : (
        <div className="w-full h-full min-h-[160px] flex items-center justify-center">
          <Award className="w-14 h-14 text-[#1e6fcf]/40" />
        </div>
      )}

      {isPdf && (
        <div className="absolute top-3 right-3 z-10 px-2 py-1 rounded bg-white/95 text-[10px] font-bold uppercase tracking-widest text-[#1758a8] shadow pointer-events-none">
          PDF
        </div>
      )}
    </div>
  );
}

export function CertificateLightbox({ cert, open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!cert || !open || cert.fileType === "pdf") return null;

  const imageUrl = cert.image || cert.fileUrl;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent showClose={false} className="max-w-5xl max-h-[92vh] overflow-hidden p-0 gap-0 border-0 bg-[#0a1a2f] text-white">
        <DialogTitle className="sr-only">{cert.title}</DialogTitle>
        <DialogDescription className="sr-only">Certificate image</DialogDescription>

        <div className="relative bg-black">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 z-20 w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 border border-white/25 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {imageUrl ? (
            <img
              src={imageUrl}
              alt={cert.title}
              className="w-full max-h-[78vh] object-contain mx-auto"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-[50vh] text-slate-400">
              <FileText className="w-14 h-14 mb-3" />
              <p className="text-sm">No certificate file available.</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 space-y-3">
          <p className="text-sm font-semibold text-white line-clamp-2">{cert.title}</p>
          {(cert.issuer || cert.year) && (
            <p className="text-xs text-slate-400">
              {cert.issuer}
              {cert.issuer && cert.year ? " · " : ""}
              {cert.year}
            </p>
          )}
          {cert.description && (
            <HtmlContent html={cert.description} className="text-xs text-slate-300 [&_p]:text-xs [&_p]:mb-2" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
